export const targets = [
  {
    domain: 'LLM Inference',
    target_text: 'KV-cache/activations 5–10× reduction',
    tolerance_text: '≤0.5–1.0% Δ accuracy',
    datasets: 'MMLU, GSM8K, MT-Bench',
    baselines: 'Quantization (INT8/INT4), Pruning, Distillation',
    hardware: 'NVIDIA A100, H100 GPUs',
    acceptance: 'Score within 0.5-1.0 percentage points of baseline on standard benchmarks',
    method_outline: 'Hierarchical multi-scale decomposition with perceptive information value criteria applied to attention mechanisms and weight matrices.'
  },
  {
    domain: 'Media Streaming',
    status: 'Validated',
    target_text: '20×+ bitrate reduction / no fidelity loss',
    tolerance_text: 'SSIM≥0.98 / ViSQOL≥4.0',
    datasets: 'Standard audio/video test sets (BBC, EBU)',
    baselines: 'AAC, HEVC, Opus codecs',
    hardware: 'x86 CPU clusters, ARM edge devices',
    acceptance: 'Perceptual quality metrics above specified thresholds in blind listening/viewing tests',
    method_outline: 'Multi-structural signal analysis preserving perceptual features while eliminating redundant information across frequency and temporal domains.'
  },
  {
    domain: 'Edge/AV Systems',
    target_text: 'p95 latency −30–50% reduction',
    tolerance_text: 'Under bandwidth/power caps',
    datasets: 'KITTI, nuScenes autonomous driving datasets',
    baselines: 'MobileNet, EfficientNet edge models',
    hardware: 'NVIDIA Jetson, Qualcomm Snapdragon',
    acceptance: 'Latency improvement validated under constrained network/power conditions with maintained safety metrics',
    method_outline: 'Edge-optimized model compression with dynamic adaptation based on available compute and network resources.'
  }
];

export const roadmap = [
  {
    title: 'M1: Market specific IDDO pipeline Developments',
    duration_weeks: 20,
    duration_display: '20 weeks per market sector',
    exit_criteria: 'Prototype LLM hooks for cache compaction pass Δaccuracy gate on MMLU and GSM8K. Documented codebase with architecture diagrams.',
    owner: 'Core Technical Leadership',
    status: 'In Progress'
  },
  {
    title: 'M2: Reproducibility Suite',
    duration_weeks: 16,
    exit_criteria: 'Public scripts/containers available. At least one third-party rerun matches results within tolerance (<1pp variance).',
    owner: 'R&D Team',
    status: 'Planned'
  },
  {
    title: 'M3: Design-Partner Pilots',
    duration_weeks: 24,
    exit_criteria: 'Two pilots (1 media CDN, 1 LLM infra) complete. Anonymized ROI ranges published. LOIs for next phase secured.',
    owner: 'Business Development',
    status: 'Planned'
  },
  {
    title: 'M4: Commercial Launch',
    duration_weeks: 28,
    exit_criteria: 'Production SDK released. First paying customers onboarded. Revenue-generating contracts signed.',
    owner: 'Executive Team',
    status: 'Future'
  }
];

export const patents = [
  // Key patents and trademarks grants
  {
    number: 'EP2745211A1',
    displayNumber: '',
    jurisdiction: 'Global jurisdictions grants, including: US, Canada, EU, India, Hong Kong, China, Russia',
    plain_claim: 'MULTI-STRUCTURAL, MULTI-LEVEL INFORMATION FORMALIZATION AND STRUCTURING METHOD, AND ASSOCIATED APPARATUS',
    link: 'https://patents.google.com/patent/EP2745211A1',
    status: 'Granted',
    category: 'Key Grant'
  },
  {
    number: 'WO2013056375A1',
    displayNumber: '',
    jurisdiction: 'Multiple jurisdictions, including US, Canada, EU',
    plain_claim: 'SYSTEM, METHOD, APPARATUS, AND COMPUTER PROGRAM PRODUCT FOR CALCULATING A SAMPLED SIGNAL',
    link: 'https://patents.google.com/patent/WO2013056375A1',
    status: 'Granted',
    category: 'Key Grant'
  },
  
  // Fortification patent applications: Filing ready
  {
    number: 'No. 61/930,623',
    jurisdiction: 'US Provisional',
    plain_claim: 'ADVANCED PHASE-LESS REPETITION CODING INTERFACE FOR MULTISTRUCTURAL, MULTI-LEVEL SOUND FORMALIZATION AND BITRATE REDUCTION SYSTEM AND METHOD USING THE SAME',
    link: '#',
    status: 'Filing Ready',
    category: 'Fortification'
  },
  {
    number: 'No. 61/930,623',
    jurisdiction: 'US Provisional',
    plain_claim: 'ADVANCED PHASE-LESS REPETITION CODING INTERFACE FOR A MULTISTRUCTURAL, MULTI-LEVEL SOUND FORMALIZATION, AND BANDWIDTH AND BIT RATE REDUCTION SYSTEM AND METHOD USING THE SAME',
    link: '#',
    status: 'Filing Ready',
    category: 'Fortification'
  },
  {
    number: 'No. 61/930,634',
    jurisdiction: 'US Provisional',
    plain_claim: 'LOSSLESS ENTROPY COMPRESSION INTERFACE FOR A MULTI-STRUCTURAL, MULTI-LEVEL SOUND FORMALIZATION, AND AUDIO BANDWIDTH AND BIT RATE REDUCTION SYSTEM AND METHOD USING THE SAME',
    link: '#',
    status: 'Filing Ready',
    category: 'Fortification'
  },
  {
    number: 'No. 61/930,646; 62/008,593',
    jurisdiction: 'US Provisional',
    plain_claim: 'AUDIO DATA LOSSLESS ACCELERATOR INTERFACE FOR A MULTI-STRUCTURAL, MULTI-LEVEL SOUND FORMALIZATION, AND ASSOCIATED SYSTEM AND METHOD FOR PROVIDING AN EXTRA COMPRESSION LAYER FOR A PREVIOUSLY REDUCED AUDIO DATA SET',
    link: '#',
    status: 'Filing Ready',
    category: 'Fortification'
  },
  {
    number: 'No. 61/930,658',
    jurisdiction: 'US Provisional',
    plain_claim: 'AUDIO DATA ADVANCED ACCELERATOR INTERFACE FOR A MULTISTRUCTURAL, MULTI-LEVEL SOUND FORMALIZATION, AND ASSOCIATED SYSTEM AND METHOD FOR EFFICIENT AUDIO DATA STREAMING',
    link: '#',
    status: 'Filing Ready',
    category: 'Fortification'
  },
  {
    number: 'No. 61/930,668',
    jurisdiction: 'US Provisional',
    plain_claim: 'AFFINE TRANSFORMS CODING INTERFACE FOR A MULTI-STRUCTURAL, MULTILEVEL SOUND FORMALIZATION, AND ASSOCIATED SYSTEM AND METHOD OF BANDWIDTH AND BIT RATE REDUCTION USING THE SAME',
    link: '#',
    status: 'Filing Ready',
    category: 'Fortification'
  },
  {
    number: 'No. 61/930,682',
    jurisdiction: 'US Provisional',
    plain_claim: 'SYSTEM AND METHOD FOR FRACTIONAL HARMONIC CORRECTIONS USING BINARY LOGARITHMIC CONSTELLATIONS REPRESENTED AS SUPERPOSITION ENERGY ADJUSTMENTS',
    link: '#',
    status: 'Filing Ready',
    category: 'Fortification'
  },
  {
    number: 'No. 61/930,697',
    jurisdiction: 'US Provisional',
    plain_claim: 'SYSTEM AND METHOD FOR DIGITAL HARMONIC FUSION SPLICING WITH BITRATE MINIMIZATION THROUGH PERCEPTUAL ENTROPY ESTIMATION USING DIGITAL SOUND SENSORS',
    link: '#',
    status: 'Filing Ready',
    category: 'Fortification'
  },
  {
    number: 'No. 61/930,271',
    jurisdiction: 'US Provisional',
    plain_claim: 'SYSTEM, METHOD, AND ASSOCIATED APPARATUS FOR ADVANCED POLYDIRECTIONAL MULTIMEDIA',
    link: '#',
    status: 'Filing Ready',
    category: 'Fortification'
  },
  {
    number: 'No. 61/984,326',
    jurisdiction: 'US Provisional',
    plain_claim: 'SYSTEM, METHOD, APPARATUS, AND COMPUTER PROGRAM PRODUCT FOR PROVIDING A GRAPHICAL USER INTERFACE FOR AN APPLICATION',
    link: '#',
    status: 'Filing Ready',
    category: 'Fortification'
  },
  {
    number: 'No. 61/989,944',
    jurisdiction: 'US Provisional',
    plain_claim: 'SYSTEM, METHOD, APPARATUS, AND COMPUTER PROGRAM PRODUCT FOR DISTRIBUTING DIGITAL CONTENT',
    link: '#',
    status: 'Filing Ready',
    category: 'Fortification'
  },
  {
    number: 'No. 62/036,356',
    jurisdiction: 'US Provisional',
    plain_claim: 'SYSTEM, METHOD, APPARATUS, AND COMPUTER PROGRAM PRODUCT FOR SYNCHRONOUS MEDIA PLAYBACK',
    link: '#',
    status: 'Filing Ready',
    category: 'Fortification'
  },
  {
    number: 'No. 62/036,316',
    jurisdiction: 'US Provisional',
    plain_claim: 'SYSTEM, METHOD, AND ASSOCIATED APPARATUS FOR ADVANCED POLYDIRECTIONAL MULTIMEDIA',
    link: '#',
    status: 'Filing Ready',
    category: 'Fortification'
  },
  {
    number: 'No. 62/036,322',
    jurisdiction: 'US Provisional',
    plain_claim: 'SYSTEM, METHOD, APPARATUS, AND COMPUTER PROGRAM PRODUCT FOR DISTRIBUTING DIGITAL MEDIA CONTENT',
    link: '#',
    status: 'Filing Ready',
    category: 'Fortification'
  },
  {
    number: 'No. 62/036,334',
    jurisdiction: 'US Provisional',
    plain_claim: 'GAME FRAMEWORK AND RELATED SYSTEMS, METHODS, AND APPARATUSES USING THE GAME FRAMEWORK',
    link: '#',
    status: 'Filing Ready',
    category: 'Fortification'
  },
  {
    number: 'No. 62/079,644',
    jurisdiction: 'US Provisional',
    plain_claim: 'SYSTEM, METHOD, APPARATUS, AND COMPUTER PROGRAM PRODUCT FOR UPDATING MEDIA CONTENT ON A DEVICE',
    link: '#',
    status: 'Filing Ready',
    category: 'Fortification'
  }
];

export const softwareAssets = [
  {
    name: 'MSML-LLMC Framework',
    description: 'An advanced R&D mathematical framework extending MSML principles to compress large AI models, particularly LLMs (Large Language Models) and other neural networks. MSML-LLMC combines techniques like hierarchical tensor decomposition, perceptual entropy pruning, and reinforcement learning-based quantization. In essence, it intelligently reduces the size of machine learning models by discarding information with low value while keeping model accuracy nearly intact. Current research demonstrated maintaining over 99.5% of original model accuracy with significantly smaller model sizes. The framework produces potential incremental compression gains that compound across model layers, yielding up to approximately 3× overall compression improvement beyond current state-of-the-art methods. Importantly, MSML-LLMC is designed for edge deployment: compressed models may be made compatible with runtime frameworks like CoreML, TensorRT, and Android NNAPI for mobile/embedded inference. By leveraging MSML\'s multi-level structures within neural networks, this IP aims to enable high-efficiency AI, large models that run faster and use less memory, without needing specialized hardware.',
    runtimes: ['PyTorch', 'ONNX', 'TensorRT', 'CoreML', 'Android NNAPI'],
    status: 'Research'
  },
  {
    name: 'D1 AUDIO AI powered Codec & Programmable Container',
    description: 'A cutting-edge novel audio codec that leverages MSML for superior compression and rich media functionality. D1 AUDIO can deliver high-resolution, fidelity-lossless sound at a fraction of normal file size, and may support 3D spatial audio and multi-channel "stems" (isolated instrument/vocal tracks) within a single file. Uniquely, it\'s paired with a specially designed programmable media container format that can hold not just audio data but synchronized metadata and other media. For example, a D1 AUDIO file can include perfectly synced lyrics, chords, and musical notations (Live Lyrics/Chords/Notes) that display in real time with the music. It can also integrate other media like album art, video snippets, or even mini-games, enabling a multilayered interactive media experience. The codec is highly optimized for streaming and storage efficiency, making it possible to enjoy studio-quality and even immersive 3D audio on bandwidth-constrained connections. In short, D1 AUDIO isn\'t just a compression codec; it\'s a platform for enriched audio-visual content, with secure and reliable packaging of media and metadata.',
    runtimes: ['FFmpeg plugin', 'Standalone CLI', 'SDK', 'Web Audio API'],
    status: 'Production'
  },
  {
    name: 'Media Fusion and Media Beam',
    description: 'Software modules that build on D1 AUDIO and MSML to create interactive, synchronous media experiences. Media Fusion allows cross-modal integration, e.g. linking pre-recorded or live streamed audio with video, lyrics, or even gameplay elements in real time. Media Beam enables broadcast-style features such as multi-room audio streaming, synchronized group listening sessions, live concerts or radio feeds relayed with minimal latency. These technologies showcase strength of the MSML powered solutions for real-time media synchronization and distribution. Media Fusion can, for instance, let a user seamlessly switch between a song\'s audio, its music video, and a karaoke lyric mode, all time-aligned. Media Beam would allow many users to experience that content together live (like a virtual concert or "live listening party"). Under the hood, these leverage MSML\'s efficiency to handle high data streams and the D1 container to keep different media types in lockstep. While entertainment-focused, the core idea of synchronizing data streams across devices/users in real time has much broad applicability.',
    runtimes: ['WebRTC', 'HLS', 'DASH', 'UDP Multicast'],
    status: 'Production'
  },
  {
    name: 'Data Steganography & LDE (Limited Data Embroidering)',
    description: 'Company behind Project Goliath has developed proprietary data embedding and content protection technology, invisible for digital data analysis and immune to audible analogue detection. One key technology is referred to as LDE, a content protection method using inaudible data embedding mechanism. LDE allows a unique digital fingerprint ("primotron" code) to be embedded directly into an audio or media file\'s fabric without affecting the perceivable output. This hidden watermark is tamper-resistant and travels with the content. It enables robust DRM (digital rights management), forensic tracking of media, and verification of authenticity. For example, each distributed copy of a song or video can carry an ID that identifies the source or licensee; any unauthorized copy can thus be traced. The steganography works in real time, meaning even live broadcasts or streaming data can have these anti-tamper fingerprints inserted. Beyond DRM, such technology can be wrapped in a solution to secure a data flow by ensuring that any alteration would destroy the embedded code and be detectable. This secure distribution methods can also involve encryption and could integrate with blockchain for content license management. Overall, these security features address content integrity and compliance, which are critical in sensitive or regulated data environments.',
    runtimes: ['Real-time Processing', 'Batch Processing', 'API Integration'],
    status: 'Production'
  },
  {
    name: 'AudioDNA Content Recognition',
    description: 'An acoustic content recognition engine capable of identifying and indexing media with high accuracy. AudioDNA can analyze an audio stream (or audiovisual content) and recognize patterns corresponding to known items, e.g. matching a song, detecting a specific sound event, or classifying content type. The technology is touted to handle not just music recognition, but also sports audio highlights, news broadcasts, live events, etc. This suggests a highly robust pattern recognition AI trained on audio features. In essence, AudioDNA functions like a Shazam-on-steroids – capable of tagging content in real time, which can power search, recommendations, or context-aware features. It relies on the Company acoustic AI know-how to remain accurate even with noise or in live scenarios.',
    runtimes: ['Cloud API', 'Edge SDK', 'Mobile Libraries'],
    status: 'Production'
  },
  {
    name: 'Cognitive Dynamic Media Cloud (cDM Cloud)',
    description: 'Cloud infrastructure platform for intelligent media processing and delivery. cDM Cloud is designed to aim for a global, low-latency content delivery network that can be combined with AI-driven data processing. It uses "brain-like" dynamic data structures (based on MSML) to store and serve content more efficiently, essentially compressing or expanding data schemas on the fly depending on context. This makes storage and retrieval highly optimized for the type of content and usage pattern. cDM Cloud also emphasizes speed, aiming for sub-10 millisecond latency for streaming media at scale. It has built-in scalability and security, including end-to-end encryption and even hooks for blockchain-based traceability. In practice, cDM Cloud strategically positioned to ingest large media (audio/video) files, apply MSML based compression and encryption, and distribute them efficiently across the globe with minimal delay. Redundancies and intelligent routing may be incorporated as a part of the system to ensure reliable delivery. This platform underpins project Goliath products by providing the heavy-lifting backend for media streaming, and content management in a cloud environment.',
    runtimes: ['REST API', 'GraphQL', 'WebSocket', 'gRPC'],
    status: 'Production'
  },
  {
    name: 'AudioMine Platform and my.ARTIST.one',
    description: 'On top of the cDM Cloud, there is AudioMine, a media engagement and monetization platform. AudioMine introduces innovative features like Attention-Reward tokens (RPM) - a gamified system rewards for user engagement (e.g. rewarding listeners for time spent, or enabling artist-specific tokens for fan loyalty). It also includes the Intelligent LiveLink Assistant (ILLA360), which uses the AudioDNA recognition tech to contextually link content across applications. For example, ILLA can detect what a user is listening to and automatically pull up synced lyrics or related content in a companion app, creating a frictionless multimedia or a second screen experience. my.ARTIST.one is a white-label "super-fan" app framework built on AudioMine and cDM Cloud. It allows artists (or content creators) to launch their own branded apps that incorporate music streaming, merchandise, NFTs, live events, and social features all in one. These apps use a dual host/satellite architecture: a main host Audiomine app provides common features, while artist-themed satellite apps (my.ARTIST.one core branded apps) plug into it. A standout feature is LiveLink functionality, delivering perfectly synchronized multimedia overlays (video, social feeds, facebook, Tik-Tok, trivia, etc.) timed to the music or content the fan is consuming. This creates immersive, interactive experiences (for instance, a fan watching a music video could get real-time behind-the-scenes pop-ups, or live comments from the artist, all synced to the playback). Additionally, the platform supports direct-to-fan commerce, exclusive content drops, and community engagement, secured by LDE data-steganography and blockchain integration ready for transactions. These capabilities demonstrate IDDO solutions strength in real-time data synchronization, user interactivity, and secure digital content ecosystems.',
    runtimes: ['iOS', 'Android', 'Web Platform', 'Blockchain Integration'],
    status: 'Production'
  }
];
